import React, { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { http, formatErr, API } from "@/lib/api";
import { useAuth, roleLabel } from "@/lib/auth";
import { FloppyDisk, Camera, User as UserIcon, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function avatarLabel(uploading, hasAvatar) {
  if (uploading) return "Uploading…";
  return hasAvatar ? "Change" : "Upload";
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", phone: "", address: "", avatar_file_id: null, password: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        address: user.address || "",
        avatar_file_id: user.avatar_file_id || null,
        password: "",
      });
    }
  }, [user]);

  const onUploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await http.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, avatar_file_id: r.data.id }));
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        address: form.address,
        avatar_file_id: form.avatar_file_id,
      };
      if (form.password) payload.password = form.password;
      await http.patch("/auth/me", payload);
      await refresh();
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("Profile updated");
      setForm((p) => ({ ...p, password: "" }));
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)} data-testid="profile-back" className="text-xs text-slate-500 hover:text-slate-900 mb-3 inline-flex items-center gap-1"><ArrowLeft size={12} /> Back</button>
      <div className="overline">Profile</div>
      <h1 className="text-3xl font-heading font-semibold tracking-tight mb-1 mt-1">My profile</h1>
      <p className="text-sm text-slate-500 mb-6">Update your contact details and avatar. Your role and store assignments are managed by an admin.</p>

      <form onSubmit={submit} className="rmms-card p-6 space-y-5" data-testid="profile-form">
        <div className="flex items-start gap-5">
          <div className="shrink-0 text-center">
            <div className="h-24 w-24 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center" data-testid="profile-avatar">
              {form.avatar_file_id ? (
                <img src={`${API}/files/${form.avatar_file_id}`} alt="" className="h-full w-full object-cover" />
              ) : (
                <UserIcon size={36} className="text-slate-400" />
              )}
            </div>
            <label className="mt-2 inline-flex items-center justify-center gap-1 text-xs cursor-pointer text-[#002FA7] hover:underline">
              <Camera size={12} />
              {avatarLabel(uploading, !!form.avatar_file_id)}
              <input ref={fileRef} type="file" accept="image/*" onChange={onUploadAvatar} data-testid="profile-avatar-input" className="hidden" />
            </label>
            {form.avatar_file_id && (
              <button type="button" onClick={() => setForm({ ...form, avatar_file_id: null })} data-testid="profile-avatar-remove" className="block mx-auto text-[10px] text-rose-600 hover:underline mt-0.5">Remove</button>
            )}
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <label className="overline mb-1.5 block">Name *</label>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" className="rmms-input" />
            </div>
            <div>
              <label className="overline mb-1.5 block">Email <span className="text-slate-400 normal-case tracking-normal">(contact admin to change)</span></label>
              <input value={user.email} disabled data-testid="profile-email-readonly" className="rmms-input opacity-60 cursor-not-allowed" />
            </div>
            <div>
              <label className="overline mb-1.5 block">Role · Store</label>
              <div className="text-sm text-slate-600">{roleLabel(user.role, user)} · {user.store_id ? <span className="font-mono-data text-[11px]">{user.store_id.slice(0, 8)}</span> : <span className="text-slate-400">—</span>}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-slate-100">
          <div>
            <label className="overline mb-1.5 block">Phone</label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone-input" className="rmms-input" placeholder="+44 ..." />
          </div>
          <div>
            <label className="overline mb-1.5 block">Address</label>
            <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="profile-address-input" className="rmms-input" placeholder="Street, City, Postcode" />
          </div>
        </div>

        <div className="pt-3 border-t border-slate-100">
          <label className="overline mb-1.5 block">Change password <span className="text-slate-400 normal-case tracking-normal">(leave blank to keep current)</span></label>
          <input type="password" minLength={6} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="profile-password-input" className="rmms-input" placeholder="New password (min 6 chars)" />
        </div>

        {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1" data-testid="profile-error">{err}</div>}

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <button type="submit" disabled={busy} data-testid="profile-save-button"
            className="h-10 px-5 rounded-md text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50" style={{ background: "#002FA7" }}>
            <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
